import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import InventoryDataService from '../../services/inventory.service';
import ProductDataService from '../../services/product.service';
import LocationDataService from '../../services/location.service';
import OriginDataService from '../../services/origin.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';

const InventoryList = () => {
  const [inventories, setInventories] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    product_id: '',
    location_id: '',
    origin_id: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t,language } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    initializeCurrency(); // Initialize currency settings from backend
    fetchInventories();
    fetchProducts();
    fetchLocations();
    fetchOrigins();
  }, [page, rowsPerPage, filters]);

  const fetchInventories = async () => {
    setLoading(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      );
      
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...cleanFilters
      };
      
      const response = await InventoryDataService.getAll(params);
      const inventoriesData = response.data?.data?.data || [];
      const totalData = response.data?.data?.total || 0;
      
      setInventories(inventoriesData);
      setTotal(totalData);
    } catch (error) {
      console.error('Error fetching inventories:', error);
      setInventories([]);
      setTotal(0);
      showSnackbar(t('inventories.errorLoadingInventories') + ': ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await ProductDataService.getAll();
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await LocationDataService.getAll();
      setLocations(response.data?.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const fetchOrigins = async () => {
    try {
      const response = await OriginDataService.getAll();
      setOrigins(response.data?.data || []);
    } catch (error) {
      console.error('Error loading origins:', error);
      setOrigins([]);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await InventoryDataService.delete(deleteDialog.id);
      showSnackbar(t('inventories.transactionDeletedSuccess'));
      fetchInventories();
    } catch (error) {
      showSnackbar(t('inventories.errorDeletingTransaction'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeColor = (type) => {
    return type === 'buy' ? 'success' : 'error';
  };

  const getAttachmentUrl = (attachment) => {
    if (!attachment) return null;
    if (attachment.startsWith('http')) return attachment;
    return `http://localhost:8080/storage/${attachment}`;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('inventories.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('inventories.buy') && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/inventories/new?type=buy')}
                >
                  {t('inventories.NewBuy')}
                </Button>
              )}
              {hasPermission('inventories.sell') && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/inventories/new?type=sell')}
                >
                  {t('inventories.NewSell')}
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                placeholder={t('inventories.searchInventories')}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="buy">{t('inventories.Buy')}</MenuItem>
                  <MenuItem value="sell">{t('inventories.Sell')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select
                  value={filters.product_id}
                  onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                  label="Product"
                >
                  <MenuItem value="">All Products</MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select
                  value={filters.location_id}
                  onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
                  label="Location"
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Origin</InputLabel>
                <Select
                  value={filters.origin_id}
                  onChange={(e) => setFilters({ ...filters, origin_id: e.target.value })}
                  label="Origin"
                >
                  <MenuItem value="">All Origins</MenuItem>
                  {origins.map(origin => (
                    <MenuItem key={origin.id} value={origin.id}>
                      {origin.name || (language === 'es' ? origin.name_es : origin.name_en)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('inventories.Date')}</TableCell>
                  <TableCell>{t('inventories.Type')}</TableCell>
                  <TableCell>{t('inventories.Product')}</TableCell>
                  <TableCell>{t('inventories.Location')}</TableCell>
                  <TableCell align="right">{t('inventories.Quantity')}</TableCell>
                  <TableCell align="right">{t('inventories.UnitPrice')}</TableCell>
                  <TableCell align="right">{t('inventories.TransactionTotal') || 'Transaction Total'}</TableCell>
                  <TableCell align="right">{t('inventories.StockTotal') || 'Stock Total'}</TableCell>
                  <TableCell>{t('inventories.Origin')}</TableCell>
                  <TableCell>{t('inventories.Attachment')}</TableCell>
                  <TableCell align="center">{t('inventories.Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : inventories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      {t('inventories.NoTransactionsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  inventories.map((inventory) => (
                    <TableRow key={inventory.id} hover>
                      <TableCell>
                        {inventory.transaction_date 
                          ? new Date(inventory.transaction_date).toLocaleDateString()
                          : new Date(inventory.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={inventory.type.toUpperCase()}
                          color={getTypeColor(inventory.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{inventory.product?.name || 'N/A'}</TableCell>
                      <TableCell>{inventory.location?.name || 'N/A'}</TableCell>
                      <TableCell align="right">
                        {inventory.type === 'buy' && inventory.remaining_inventory !== undefined ? (
                          <Tooltip 
                            title={`${t('inventories.RemainingInventory') || 'Remaining Inventory'}: ${parseFloat(inventory.remaining_inventory || 0).toFixed(2)}`}
                            arrow
                            placement="top"
                          >
                            <span style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
                              {parseFloat(inventory.quantity).toFixed(2)}
                            </span>
                          </Tooltip>
                        ) : (
                          parseFloat(inventory.quantity).toFixed(2)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(inventory.unit_price)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(inventory.transaction_total || inventory.total_price || inventory.quantity * inventory.unit_price)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(inventory.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {inventory.origin?.name || (inventory.origin ? (language === 'es' ? inventory.origin.name_es : inventory.origin.name_en) : '-')}
                      </TableCell>
                      <TableCell>
                        {inventory.buy_order_attachment_url || inventory.buy_order_attachment ? (
                          <IconButton
                            size="small"
                            onClick={() => window.open(getAttachmentUrl(inventory.buy_order_attachment_url || inventory.buy_order_attachment), '_blank')}
                          >
                            <DownloadIcon />
                          </IconButton>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/inventories/${inventory.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(inventory.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => 
              t('pagination.displayedRows', { from, to, count })
            }
          />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this transaction?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryList;
